import {Search, X} from 'lucide-react';
import * as React from 'react';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';

type Props = {
  initialQuery: string | null;
  onSearch: (query: string) => void;
  inputProps?: React.ComponentProps<typeof TextInput>;
};

export const SearchInput = (props: Props) => {
  const {initialQuery, onSearch, inputProps} = props;

  const [query, setQuery] = React.useState(initialQuery || '');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // The box holds a draft, so `initialQuery` seeds it rather than controlling
  // it — but the applied query can also change from *outside* (Reset filters,
  // or a back navigation onto an earlier filter state), and a box still
  // showing the query that is no longer applied is a lie about what the list
  // below is showing. Re-seed on those, and only those: the ref records what
  // this box last submitted, so a prop merely echoing our own submission
  // leaves in-progress typing alone.
  const submittedRef = React.useRef(initialQuery || '');
  React.useEffect(() => {
    const applied = initialQuery || '';
    if (applied !== submittedRef.current) {
      submittedRef.current = applied;
      setQuery(applied);
    }
  }, [initialQuery]);

  // Submitting via a real <form> is the only signal reliably fired by mobile
  // soft keyboards (Android's "Go"/search action button in particular), which
  // do not always dispatch an Enter keydown. stopPropagation keeps the submit
  // from bubbling to an enclosing form (e.g. when this renders inside another
  // modal's form) and triggering its onSubmit.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    submittedRef.current = query;
    onSearch(query);
  };

  // Clearing both empties the box and drops the active search — a typed-but-
  // unsubmitted query and an applied one look the same in the input, and
  // resetting to an applied filter the user is trying to remove helps nobody.
  const handleClear = () => {
    setQuery('');
    submittedRef.current = '';
    onSearch('');
    inputRef.current?.focus();
  };

  return (
    <form onSubmit={handleSubmit} className="relative flex flex-row">
      <Search className="text-muted-foreground absolute top-1/2 left-2 size-4 -translate-y-1/2" />
      <TextInput
        {...inputProps}
        ref={inputRef}
        enterKeyHint="search"
        value={query}
        onChange={setQuery}
        className={cn('pl-8', query && 'pr-8')}
      />
      {query && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer p-1.5"
        >
          <X className="size-4" />
        </button>
      )}
      <button style={{display: 'none'}} type="submit">
        Search
      </button>
    </form>
  );
};
